import type { ApiError } from '@/shared/types/api';
import { formatValidationMessages } from '@/shared/utils/validationMessages';
import { useCallback, useEffect, useRef, useState } from 'react';

interface ErrorHandlerOptions {
  defaultMessage?: string;
  onError?: (error: Error | ApiError) => void;
}

export const useErrorHandler = (options: ErrorHandlerOptions = {}) => {
  const [error, setError] = useState<string | null>(null);
  const optionsRef = useRef(options);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const handleError = useCallback((err: unknown) => {
    const { defaultMessage = 'An error occurred', onError } = optionsRef.current;
    let errorMessage: string;

    // Check for 401 Unauthorized errors specifically
    if (typeof err === 'object' && err !== null && 'status' in err && err.status === 401) {
      errorMessage =
        'You do not have permission to perform this action. Please contact your administrator.';
    } else if (err instanceof Error) {
      errorMessage = err.message;
    } else if (typeof err === 'object' && err !== null) {
      // Check for validation errors (422) with nested error structure
      const errorObj = err as ApiError;

      // Check if it's an ApiError with response structure (from axios interceptor)
      if (errorObj.status === 422 && errorObj.response?.data) {
        const errors = errorObj.response.data.errors || errorObj.response.data.error || {};
        const errorMessages: string[] = [];

        if (typeof errors === 'object' && !Array.isArray(errors)) {
          Object.keys(errors).forEach((field) => {
            const messages = errors[field];
            if (Array.isArray(messages)) {
              messages.forEach((msg: string) => {
                errorMessages.push(msg);
              });
            } else if (typeof messages === 'string') {
              errorMessages.push(messages);
            }
          });
        } else if (typeof errors === 'string') {
          errorMessages.push(errors);
        }

        errorMessage =
          errorMessages.length > 0
            ? formatValidationMessages(errorMessages)
            : errorObj.message || 'Validation failed. Please check your input.';
      } else if (errorObj.response?.data?.errors) {
        // Handle axios errors with response.data.errors (Laravel validation format)
        const errors = errorObj.response.data.errors;
        const errorMessages: string[] = [];

        Object.keys(errors).forEach((field) => {
          const messages = errors[field];
          if (Array.isArray(messages)) {
            messages.forEach((msg: string) => {
              errorMessages.push(msg);
            });
          } else if (typeof messages === 'string') {
            errorMessages.push(messages);
          }
        });

        errorMessage =
          errorMessages.length > 0
            ? formatValidationMessages(errorMessages)
            : 'Validation failed. Please check your input.';
      } else if ('message' in errorObj) {
        errorMessage = errorObj.message;
      } else {
        errorMessage = defaultMessage;
      }
    } else if (typeof err === 'string') {
      errorMessage = err;
    } else {
      errorMessage = defaultMessage;
    }

    setError(null);
    setTimeout(() => setError(errorMessage), 0);

    onError?.(err as Error | ApiError);

    console.error('Error occurred:', err);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    error,
    handleError,
    clearError,
  };
};
